import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getMilkBatches, calcPearson, createStdPlan, getStdPlans, deleteStdPlan, fmt } from "@/lib/api";
import { Calculator, Droplets, AlertTriangle, ArrowRight, Save, Trash2, BookmarkCheck, Timer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nContext";

// Format a duration in hours into a readable "Xh Ym" string.
const formatDuration = (hours) => {
  if (hours == null || !Number.isFinite(hours) || hours < 0) return "-";
  const totalMin = Math.round(hours * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};

const NumField = ({ id, label, unit, value, onChange, step = "0.01", testid, disabled }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <Input
        id={id}
        type="number"
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="pr-12 text-right font-mono tabular-nums"
      />
      {unit && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {unit}
        </span>
      )}
    </div>
  </div>
);

// Pearson Square SVG diagram
const PearsonDiagram = ({ res, mode }) => {
  const { t } = useT();
  if (!res || !res.valid) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-border text-sm text-muted-foreground">
        {t("fat.enterValuesDiagram")}
      </div>
    );
  }
  const sq = res.square;
  const otherLabel = mode === "cream" ? t("fat.cream") : t("fat.skim");
  return (
    <svg viewBox="0 0 320 280" className="mx-auto w-full max-w-[360px]">
      {/* diagonals */}
      <line x1="70" y1="60" x2="160" y2="140" className="pearson-line" />
      <line x1="70" y1="220" x2="160" y2="140" className="pearson-line" />
      <line x1="160" y1="140" x2="250" y2="60" className="pearson-line" />
      <line x1="160" y1="140" x2="250" y2="220" className="pearson-line" />

      {/* Whole milk node (top-left) */}
      <g>
        <circle cx="70" cy="60" r="34" fill="hsl(var(--brand) / 0.12)" stroke="hsl(var(--brand))" strokeWidth="1.5" />
        <text x="70" y="55" textAnchor="middle" fill="hsl(var(--foreground))" style={{ fontSize: 9 }}>{t("fat.whole")}</text>
        <text x="70" y="71" textAnchor="middle" fill="hsl(var(--foreground))" className="font-mono" style={{ fontSize: 13, fontWeight: 600 }}>{`${fmt(sq.whole_milk_fat, 2)}%`}</text>
      </g>

      {/* Other node (bottom-left) */}
      <g>
        <circle cx="70" cy="220" r="34" fill="hsl(var(--muted))" stroke="hsl(var(--muted-foreground))" strokeWidth="1.5" />
        <text x="70" y="215" textAnchor="middle" fill="hsl(var(--foreground))" style={{ fontSize: 9 }}>{otherLabel}</text>
        <text x="70" y="231" textAnchor="middle" fill="hsl(var(--foreground))" className="font-mono" style={{ fontSize: 13, fontWeight: 600 }}>{`${fmt(sq.other_fat, 2)}%`}</text>
      </g>

      {/* Target node (center) */}
      <g>
        <circle cx="160" cy="140" r="36" fill="hsl(var(--accent) / 0.15)" stroke="hsl(var(--accent))" strokeWidth="2" />
        <text x="160" y="135" textAnchor="middle" fill="hsl(var(--foreground))" style={{ fontSize: 9 }}>{t("fat.target")}</text>
        <text x="160" y="152" textAnchor="middle" fill="hsl(var(--foreground))" className="font-mono" style={{ fontSize: 14, fontWeight: 700 }}>{`${fmt(sq.target_fat, 2)}%`}</text>
      </g>

      {/* Parts whole (top-right) */}
      <g>
        <rect x="222" y="42" width="56" height="36" rx="8" fill="hsl(var(--brand) / 0.10)" />
        <text x="250" y="60" textAnchor="middle" fill="hsl(var(--foreground))" style={{ fontSize: 8 }}>{t("fat.partsWhole")}</text>
        <text x="250" y="72" textAnchor="middle" fill="hsl(var(--foreground))" className="font-mono" style={{ fontSize: 12, fontWeight: 700 }}>{fmt(sq.parts_whole_milk, 2)}</text>
      </g>

      {/* Parts other (bottom-right) */}
      <g>
        <rect x="222" y="202" width="56" height="36" rx="8" fill="hsl(var(--muted))" />
        <text x="250" y="220" textAnchor="middle" fill="hsl(var(--foreground))" style={{ fontSize: 8 }}>{`${t("fat.parts")} ${otherLabel.toLowerCase()}`}</text>
        <text x="250" y="232" textAnchor="middle" fill="hsl(var(--foreground))" className="font-mono" style={{ fontSize: 12, fontWeight: 700 }}>{fmt(sq.parts_other, 2)}</text>
      </g>
    </svg>
  );
};

export default function FatCalculator() {
  const { t } = useT();
  const [batches, setBatches] = useState([]);
  const [selectedId, setSelectedId] = useState("manual");
  const [mode, setMode] = useState("skim");
  const [milkFat, setMilkFat] = useState("4.0");
  const [targetFat, setTargetFat] = useState("3.0");
  const [baseVolume, setBaseVolume] = useState("1000");
  const [skimFat, setSkimFat] = useState("0.05");
  const [creamFat, setCreamFat] = useState("40");
  const [separatorRate, setSeparatorRate] = useState("1000");
  const [res, setRes] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [planName, setPlanName] = useState("");

  const loadPlans = () => getStdPlans().then(setSavedPlans);
  useEffect(() => {
    loadPlans();
  }, []);

  const savePlan = async () => {
    if (!planName.trim()) {
      toast.error(t("fat.giveName"));
      return;
    }
    try {
      await createStdPlan({
        name: planName,
        milk_batch_id: selectedId !== "manual" ? selectedId : null,
        milk_fat: Number(milkFat),
        target_fat: Number(targetFat),
        mode,
        skim_fat: Number(skimFat) || 0,
        cream_fat: Number(creamFat) || 40,
        base_volume: Number(baseVolume),
        result: res || {},
      });
      toast.success(t("fat.planSaved"));
      setSaveOpen(false);
      setPlanName("");
      loadPlans();
    } catch {
      toast.error(t("fat.failedSavePlan"));
    }
  };

  const removePlan = async (id) => {
    await deleteStdPlan(id);
    toast.success(t("planning.planRemoved"));
    loadPlans();
  };

  useEffect(() => {
    getMilkBatches().then(setBatches);
  }, []);

  const selectedBatch = useMemo(
    () => batches.find((b) => b.id === selectedId),
    [batches, selectedId]
  );

  const onSelectBatch = (id) => {
    setSelectedId(id);
    if (id !== "manual") {
      const b = batches.find((x) => x.id === id);
      if (b?.analysis?.fat != null) setMilkFat(String(b.analysis.fat));
      if (b?.remaining_l != null) setBaseVolume(String(b.remaining_l));
    }
  };

  useEffect(() => {
    const mf = Number(milkFat);
    const tf = Number(targetFat);
    const bv = Number(baseVolume);
    if ([mf, tf, bv].some((n) => Number.isNaN(n)) || bv <= 0) {
      setRes(null);
      return;
    }
    const timer = setTimeout(() => {
      calcPearson({
        milk_fat: mf,
        target_fat: tf,
        base_volume: bv,
        mode,
        skim_fat: Number(skimFat) || 0,
        cream_fat: Number(creamFat) || 40,
      })
        .then(setRes)
        .catch(() => setRes(null));
    }, 250);
    return () => clearTimeout(timer);
  }, [milkFat, targetFat, baseVolume, mode, skimFat, creamFat]);

  // Auto-suggest mode based on target vs milk
  useEffect(() => {
    const mf = Number(milkFat);
    const tf = Number(targetFat);
    if (!Number.isNaN(mf) && !Number.isNaN(tf)) {
      if (tf > mf && mode === "skim") setMode("cream");
      if (tf < mf && mode === "cream") setMode("skim");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetFat, milkFat]);

  return (
    <Layout>
      <div className="header-band -mx-3 -mt-4 mb-5 rounded-b-2xl px-3 py-5 sm:-mx-6 sm:-mt-6 sm:mb-6 sm:px-6 sm:py-6 lg:-mx-8 lg:px-8">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-[hsl(var(--brand))]" />
          <h2 className="text-2xl font-semibold tracking-tight">{t("fat.title")}</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("fat.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Inputs */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle className="text-base">{t("fat.inputs")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("fat.milkBatch")}</Label>
              <Select value={selectedId} onValueChange={onSelectBatch}>
                <SelectTrigger data-testid="pearson-batch-select">
                  <SelectValue placeholder={t("fat.selectBatch")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t("stock.manualEntry")}</SelectItem>
                  {batches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.batch_code} · {fmt(b.analysis?.fat, 2)}% · {fmt(b.remaining_l)}L
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>{t("fat.mode")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={mode === "skim" ? "default" : "outline"}
                  onClick={() => setMode("skim")}
                  data-testid="pearson-mode-skim"
                  className="w-full"
                >
                  {t("fat.wholeSkim")}
                </Button>
                <Button
                  type="button"
                  variant={mode === "cream" ? "default" : "outline"}
                  onClick={() => setMode("cream")}
                  data-testid="pearson-mode-cream"
                  className="w-full"
                >
                  {t("fat.wholeCream")}
                </Button>
              </div>
            </div>

            <NumField id="milkfat" label={t("fat.milkFat")} unit="%" value={milkFat} onChange={setMilkFat} testid="pearson-milkfat-input" disabled={selectedId !== "manual"} />
            <NumField id="target" label={t("fat.targetFat")} unit="%" value={targetFat} onChange={setTargetFat} testid="pearson-target-fat-input" />
            <NumField id="base" label={t("fat.milkVolume")} unit="L" step="1" value={baseVolume} onChange={setBaseVolume} testid="pearson-base-volume-input" />

            <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
              <NumField id="skimfat" label={t("fat.skimFat")} unit="%" value={skimFat} onChange={setSkimFat} testid="pearson-skimfat-input" />
              <NumField id="creamfat" label={t("fat.creamFat")} unit="%" value={creamFat} onChange={setCreamFat} testid="pearson-creamfat-input" />
            </div>

            <div className="space-y-1.5 border-t border-border pt-3">
              <NumField id="separator" label={t("fat.separatorCapacity")} unit="L/h" step="50" value={separatorRate} onChange={setSeparatorRate} testid="pearson-separator-rate-input" />
              <p className="text-xs text-muted-foreground">{t("fat.separatorHint")}</p>
            </div>
          </CardContent>
        </Card>

        {/* Diagram */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle className="text-base">{t("fat.pearsonSquare")}</CardTitle>
          </CardHeader>
          <CardContent>
            <PearsonDiagram res={res} mode={mode} />
            {res?.valid && (
              <div className="mt-3 rounded-lg bg-secondary p-3 text-center">
                <div className="text-xs text-muted-foreground">{t("fat.blendRatio")}</div>
                <div className="font-mono text-lg font-semibold" data-testid="pearson-ratio-output">{res.ratio}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle className="text-base">{t("fat.results")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!res && (
              <p className="py-8 text-center text-sm text-muted-foreground">{t("fat.enterValid")}</p>
            )}
            {res && !res.valid && (
              <Alert variant="destructive" data-testid="pearson-invalid-alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t("fat.cannotStandardize")}</AlertTitle>
                <AlertDescription>{res.message}</AlertDescription>
              </Alert>
            )}
            {res?.valid && (
              <>
                <div className="rounded-xl border border-[hsl(var(--brand)/0.25)] bg-[hsl(var(--brand)/0.06)] p-4" data-testid="pearson-blend-output">
                  <div className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--brand))]">
                    <Droplets className="h-4 w-4" /> {t("fat.blendPlan")}
                  </div>
                  <div className="mt-2 space-y-1.5 text-sm">
                    <Row label={t("fat.wholeMilk")} value={`${fmt(res.base_volume)} L`} />
                    <Row label={t("fat.addType", { type: res.add_type })} value={`${fmt(res.add_volume)} L`} strong />
                    <Row label={t("fat.totalVolume")} value={`${fmt(res.total_volume)} L`} />
                    <Row label={t("fat.resultingFat")} value={`${fmt(res.resulting_fat, 2)} %`} strong />
                  </div>
                </div>

                {res.degrease && (
                  <div className="rounded-xl border border-amber-300 bg-[hsl(var(--qc-warn-bg))] p-4" data-testid="pearson-degrease-output">
                    <div className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--warning))]">
                      <ArrowRight className="h-4 w-4" /> {t("fat.degreasePlan")}
                    </div>
                    <div className="mt-2 space-y-1.5 text-sm">
                      <Row label={t("fat.separateCream")} value={`${fmt(res.degrease.cream_to_remove_l)} L`} strong />
                      <Row label={t("fat.creamFat")} value={`${fmt(res.degrease.cream_fat, 1)} %`} />
                      <Row label={t("fat.remainingAtTarget")} value={`${fmt(res.degrease.remaining_volume_l)} L`} strong />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{res.degrease.note}</p>
                  </div>
                )}

                {res.degrease && Number(separatorRate) > 0 && (
                  <div className="rounded-xl border border-[hsl(var(--brand)/0.25)] bg-[hsl(var(--brand)/0.06)] p-4" data-testid="pearson-separator-output">
                    <div className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--brand))]">
                      <Timer className="h-4 w-4" /> {t("fat.liveRunTime")}
                    </div>
                    <div className="mt-2 space-y-1.5 text-sm">
                      <Row label={t("fat.milkToRun")} value={`${fmt(res.degrease.milk_to_separate_l)} L`} strong />
                      <Row label={t("fat.separatorCapacity")} value={`${fmt(separatorRate)} L/h`} />
                      <Row
                        label={t("fat.runTime")}
                        value={formatDuration(res.degrease.milk_to_separate_l / Number(separatorRate))}
                        strong
                      />
                      <Row label={t("fat.creamRemoved")} value={`${fmt(res.degrease.cream_to_remove_l)} L @ ${fmt(res.degrease.cream_fat, 1)}%`} />
                      <Row label={t("fat.skimProduced")} value={`${fmt(res.degrease.skim_produced_l)} L @ ${fmt(res.degrease.skim_fat, 2)}%`} />
                      <Row label={t("fat.bypass")} value={`${fmt(res.degrease.bypass_volume_l)} L`} />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground" data-testid="pearson-separator-summary">
                      {t("fat.separatorSummary", { rate: fmt(separatorRate), time: formatDuration(res.degrease.milk_to_separate_l / Number(separatorRate)), sep: fmt(res.degrease.milk_to_separate_l), total: fmt(res.base_volume), cream: fmt(res.degrease.cream_to_remove_l), bypass: fmt(res.degrease.bypass_volume_l), remaining: fmt(res.degrease.remaining_volume_l), target: fmt(res.target_fat, 2) })}
                    </p>
                  </div>
                )}
                <Button variant="outline" className="w-full" onClick={() => setSaveOpen(true)} data-testid="pearson-save-button">
                  <Save className="mr-1.5 h-4 w-4" /> {t("fat.savePlanBtn")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cream fat calculator */}
      <CreamFatCalculator />

      {/* Saved plans */}
      {savedPlans.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BookmarkCheck className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("fat.savedPlans")} ({savedPlans.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {savedPlans.map((sp) => (
                <div key={sp.id} className="flex items-start justify-between rounded-lg border border-border p-3" data-testid={`stdplan-card-${sp.id}`}>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{sp.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {fmt(sp.milk_fat, 2)}% → {fmt(sp.target_fat, 2)}% · {sp.mode}
                    </div>
                    {sp.batch_code && <div className="text-xs text-muted-foreground">{sp.batch_code}</div>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removePlan(sp.id)} data-testid={`stdplan-delete-${sp.id}`}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {t("fat.savedPlansHint")}
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("fat.savePlanBtn")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("fat.planName")}</Label>
            <Input value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder={t("fat.planNamePlaceholder")} data-testid="stdplan-name-input" />
            {res?.valid && (
              <p className="font-mono text-xs text-muted-foreground">
                {t("fat.previewLine", { from: fmt(milkFat, 2), to: fmt(targetFat, 2), vol: fmt(res.add_volume), type: res.add_type })}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={savePlan} data-testid="stdplan-save-button">{t("planning.savePlan")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono tabular-nums ${strong ? "font-semibold" : ""}`}>{value}</span>
  </div>
);

// Independent calculator: given milk fat, skim (degreased) milk fat, the volume of milk
// degreased and the volume of cream obtained, compute the fat content of that cream via
// a fat mass-balance:  Vmilk·Fmilk = Vskim·Fskim + Vcream·Fcream.
function CreamFatCalculator() {
  const { t } = useT();
  const [milkFat, setMilkFat] = useState("4.0");
  const [skimFat, setSkimFat] = useState("0.05");
  const [milkVol, setMilkVol] = useState("1000");
  const [creamVol, setCreamVol] = useState("60");

  const calc = useMemo(() => {
    const Fm = Number(milkFat), Fs = Number(skimFat), Vm = Number(milkVol), Vc = Number(creamVol);
    if ([Fm, Fs, Vm, Vc].some((n) => Number.isNaN(n)) || milkFat === "" || skimFat === "" || milkVol === "" || creamVol === "") {
      return { state: "empty" };
    }
    if (Vm <= 0 || Vc <= 0 || Vc >= Vm) return { state: "invalid" };
    const Vs = Vm - Vc;
    const creamFat = (Vm * Fm - Vs * Fs) / Vc;
    const plausible = creamFat > Fm && creamFat <= 100;
    return { state: "ok", skimVol: Vs, creamFat, plausible };
  }, [milkFat, skimFat, milkVol, creamVol]);

  return (
    <Card className="mt-6" data-testid="cream-calc-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Droplets className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("fat.creamCalcTitle")}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t("fat.creamCalcSubtitle")}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="grid grid-cols-2 gap-3">
            <NumField id="cf-milkfat" label={t("fat.cfMilkFat")} unit="%" value={milkFat} onChange={setMilkFat} testid="cream-milkfat-input" />
            <NumField id="cf-skimfat" label={t("fat.cfSkimFat")} unit="%" value={skimFat} onChange={setSkimFat} testid="cream-skimfat-input" />
            <NumField id="cf-milkvol" label={t("fat.cfMilkVol")} unit="L" step="1" value={milkVol} onChange={setMilkVol} testid="cream-milkvol-input" />
            <NumField id="cf-creamvol" label={t("fat.cfCreamVol")} unit="L" step="1" value={creamVol} onChange={setCreamVol} testid="cream-creamvol-input" />
          </div>
          <div>
            {calc.state === "empty" && (
              <p className="py-8 text-center text-sm text-muted-foreground">{t("fat.cfEnter")}</p>
            )}
            {calc.state === "invalid" && (
              <Alert variant="destructive" data-testid="cream-invalid-alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t("fat.cfInvalidTitle")}</AlertTitle>
                <AlertDescription>{t("fat.cfInvalidVol")}</AlertDescription>
              </Alert>
            )}
            {calc.state === "ok" && (
              <div className="rounded-xl border border-[hsl(var(--brand)/0.25)] bg-[hsl(var(--brand)/0.06)] p-4">
                <div className="text-xs text-muted-foreground">{t("fat.cfResult")}</div>
                <div className="font-mono text-3xl font-bold text-foreground" data-testid="cream-fat-output">{fmt(calc.creamFat, 2)} %</div>
                <div className="mt-3 space-y-1.5 border-t border-border pt-3 text-sm">
                  <Row label={t("fat.cfSkimVol")} value={`${fmt(calc.skimVol)} L`} />
                  <Row label={t("fat.cfCreamVol")} value={`${fmt(Number(creamVol))} L`} strong />
                </div>
                {!calc.plausible && (
                  <p className="mt-2 text-xs text-[hsl(var(--warning))]" data-testid="cream-implausible">{t("fat.cfImplausible")}</p>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">{t("fat.cfHint")}</p>
      </CardContent>
    </Card>
  );
}
